import { create } from 'zustand'
import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS, type UserActivatedPayload } from '@/services/analytics/events'
import { setMilestone } from '@/services/analytics/milestones'
import { buildLsUserKey } from '@/storage/version'
import { activeOnboardingConfig } from './active-config'

/**
 * Per-user onboarding state: interview answers (timestamped for funnel data),
 * resolved persona, completion/skip markers, and first-week checklist state.
 *
 * SOURCE OF TRUTH is the server (the `onboarding` key on the user profile in
 * edge-user-api, like `learnProgress`); localStorage is only the optimistic
 * cache. Synced by use-onboarding-server-sync.ts. Bounded by construction:
 * a finite question set + one checklist, so the cache key stays tiny.
 *
 * The cache is per user (`edge:{scope}:onboarding:state`, scope =
 * storageScopeId of the signed-in user, like the IDB workspace cache). The
 * sync hook pushes the scope in with `setScope`; until then, and after
 * logout, the store is EMPTY and persists nothing. The old browser-wide
 * `edge:onboarding:state` key is deliberately neither read nor deleted.
 *
 * Spec: docs/superpowers/specs/2026-08-21-onboarding-experience-design.md (D6).
 *
 * This store also EMITS the onboarding funnel. It is the only place that
 * sees every path through the flow (checklist signals arrive from five
 * unrelated files), so analytics lives here and not in the components.
 * `hydrate()` and the `readPersisted()` behind `setScope()` replay stored
 * state and must never emit, or the whole funnel re-fires on every login.
 * Plan: docs/superpowers/plans/2026-09-09-posthog-observability-plan.md, task 5.
 */

/** Current storage scope; null = anonymous (signed out), nothing persists. */
let scope: string | null = null

/**
 * Scopes whose server copy was applied (or confirmed absent) in this page
 * lifetime. Server-wins happens once per scope: re-entering one (back from
 * /billing, which unmounts the sync hook) keeps its local cache, which every
 * commit persists, over the login-time profile the account store still holds.
 */
const hydratedScopes = new Set<string>()

function storageKey(): string | null {
  return scope ? buildLsUserKey(scope, 'onboarding', 'state') : null
}

export type OnboardingAnswer = {
  optionId: string
  /** ISO timestamp of when this answer was chosen (funnel/abandonment data). */
  at: string
}

export type OnboardingChecklistState = {
  id: string
  done: string[]
  /** Item id to ISO completion time. Parallel to `done`; enables retroactive analysis. */
  doneAt: Record<string, string>
  /** Progress toward multi-step items (e.g. watchlist-3), keyed by item id. */
  counts: Record<string, number>
  /** Permanent hide ("Hide checklist", or "Nice work" once all done). */
  dismissedAt: string | null
  /** Folded into the corner pill by the card's X; reopens from the pill. */
  minimized: boolean
}

/**
 * The three first-time behaviors that define "fully onboarded" (task 5c).
 * Two of three within the window is activated; the window is applied in
 * PostHog, not here, so it can be retuned without a deploy.
 */
export type ActivationBehavior = 'chart_opened' | 'custom_scan' | 'watchlist_ticker_added'

export type OnboardingActivationState = {
  /** Behavior to ISO first-occurrence time. */
  behaviors: Partial<Record<ActivationBehavior, string>>
  /** Stamped when the second distinct behavior lands; the once-only guard. */
  activatedAt: string | null
}

const ACTIVATION_THRESHOLD = 2

// A `type` (not interface) so it's assignable to Record<string, unknown> when
// synced through the user-profile update mutation (mirrors LearnProgress).
export type OnboardingState = {
  /** Which config version produced these answers (null = never started). */
  configVersion: number | null
  answers: Record<string, OnboardingAnswer>
  persona: string | null
  startedAt: string | null
  /** Set when the interview finished; null while in progress or abandoned. */
  completedAt: string | null
  /** True for the legacy "Skip, take me in" (not offered since 2026-10-24; kept for historical records). */
  skipped: boolean
  checklist: OnboardingChecklistState | null
  /** Panel kinds whose one-time first-touch tour offer has been shown. */
  tipsSeen: string[]
  /**
   * Server-synced so the once-only `user_activated` guard survives a user
   * switching devices, which a local flag would not.
   */
  activation: OnboardingActivationState
  /**
   * Who wrote the record when the app did not: 'migration' for the legacy
   * account migration. Carried through validation and the synced snapshot
   * so a local change never strips it from the server copy.
   */
  origin: string | null
}

export const EMPTY_ONBOARDING: OnboardingState = {
  configVersion: null,
  answers: {},
  persona: null,
  startedAt: null,
  completedAt: null,
  skipped: false,
  checklist: null,
  tipsSeen: [],
  activation: { behaviors: {}, activatedAt: null },
  origin: null,
}

/** Validate an untrusted (server / localStorage) value into OnboardingState. */
export function validateOnboardingState(raw: unknown): OnboardingState | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const answers: Record<string, OnboardingAnswer> = {}
  if (r.answers != null && typeof r.answers === 'object') {
    for (const [qid, val] of Object.entries(r.answers as Record<string, unknown>)) {
      if (val != null && typeof val === 'object') {
        const v = val as Record<string, unknown>
        if (typeof v.optionId === 'string' && typeof v.at === 'string') {
          answers[qid] = { optionId: v.optionId, at: v.at }
        }
      }
    }
  }
  let checklist: OnboardingChecklistState | null = null
  if (r.checklist != null && typeof r.checklist === 'object') {
    const c = r.checklist as Record<string, unknown>
    if (typeof c.id === 'string') {
      const counts: Record<string, number> = {}
      if (c.counts != null && typeof c.counts === 'object') {
        for (const [k, v] of Object.entries(c.counts as Record<string, unknown>)) {
          if (typeof v === 'number' && Number.isFinite(v)) counts[k] = v
        }
      }
      // Older server payloads predate `doneAt`; treat absence as empty
      // rather than invalidating the whole state.
      const doneAt: Record<string, string> = {}
      if (c.doneAt != null && typeof c.doneAt === 'object') {
        for (const [k, v] of Object.entries(c.doneAt as Record<string, unknown>)) {
          if (typeof v === 'string') doneAt[k] = v
        }
      }
      checklist = {
        id: c.id,
        done: Array.isArray(c.done) ? c.done.filter((d): d is string => typeof d === 'string') : [],
        doneAt,
        counts,
        dismissedAt: typeof c.dismissedAt === 'string' ? c.dismissedAt : null,
        // Older records predate minimize; absent reads as expanded.
        minimized: c.minimized === true,
      }
    }
  }
  const activation: OnboardingActivationState = { behaviors: {}, activatedAt: null }
  if (r.activation != null && typeof r.activation === 'object') {
    const a = r.activation as Record<string, unknown>
    if (a.behaviors != null && typeof a.behaviors === 'object') {
      for (const [k, v] of Object.entries(a.behaviors as Record<string, unknown>)) {
        if (typeof v === 'string') activation.behaviors[k as ActivationBehavior] = v
      }
    }
    if (typeof a.activatedAt === 'string') activation.activatedAt = a.activatedAt
  }
  return {
    configVersion: typeof r.configVersion === 'number' ? r.configVersion : null,
    answers,
    persona: typeof r.persona === 'string' ? r.persona : null,
    startedAt: typeof r.startedAt === 'string' ? r.startedAt : null,
    completedAt: typeof r.completedAt === 'string' ? r.completedAt : null,
    skipped: r.skipped === true,
    checklist,
    tipsSeen: Array.isArray(r.tipsSeen)
      ? r.tipsSeen.filter((t): t is string => typeof t === 'string')
      : [],
    activation,
    origin: typeof r.origin === 'string' ? r.origin : null,
  }
}

function readPersisted(): OnboardingState {
  const key = storageKey()
  if (!key) return EMPTY_ONBOARDING
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return EMPTY_ONBOARDING
    return validateOnboardingState(JSON.parse(raw)) ?? EMPTY_ONBOARDING
  } catch {
    return EMPTY_ONBOARDING
  }
}

function persist(s: OnboardingState): void {
  const key = storageKey()
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(s))
  } catch {
    /* ignore quota / unavailable storage */
  }
}

/** Extract the syncable snapshot from the full store state. */
export function onboardingSnapshot(s: OnboardingState): OnboardingState {
  return {
    configVersion: s.configVersion,
    answers: s.answers,
    persona: s.persona,
    startedAt: s.startedAt,
    completedAt: s.completedAt,
    skipped: s.skipped,
    checklist: s.checklist,
    tipsSeen: s.tipsSeen,
    activation: s.activation,
    origin: s.origin,
  }
}

interface OnboardingStoreState extends OnboardingState {
  /**
   * The current scope's server copy has been applied (or confirmed absent),
   * now or earlier in this page lifetime. Never persisted or synced. The interview must not choose its door or
   * stamp startedAt before this, or a fresh browser re-runs a finished
   * interview and fires onboarding_started into a record about to be replaced.
   */
  hydrated: boolean
  /**
   * Point the cache at the signed-in user (storageScopeId) and load their
   * stored state, silently. null or 'anonymous' (logout) resets to EMPTY.
   */
  setScope: (scopeId: string | null) => void
  /** Replace local state with the authoritative server value (login sync). */
  hydrate: (state: OnboardingState) => void
  /**
   * No server value to apply: keep local, mark hydrated. `provisional` (the
   * profile request failed) opens the door without remembering the scope,
   * so its server copy is still applied when a profile does load.
   */
  markHydrated: (opts?: { provisional?: boolean }) => void
  /** Interview shown for the first time: stamp startedAt + config version. */
  begin: (configVersion: number) => void
  /** Record one answer as it is chosen (timestamped). */
  recordAnswer: (questionId: string, optionId: string) => void
  /** Interview finished: stamp persona + completedAt, activate a checklist. */
  complete: (persona: string, checklistId: string | null) => void
  /** Legacy "Skip, take me in" (not offered since 2026-10-24): mark skipped + completedAt without a persona. */
  skipAll: () => void
  /** Mark a checklist item done (idempotent). */
  markChecklistDone: (itemId: string) => void
  /** Un-mark a manually ticked item (card checkbox toggle). */
  unmarkChecklistDone: (itemId: string) => void
  /**
   * Bump a signal-driven item's count; auto-marks done at `target`.
   * No-op once done.
   */
  incrementChecklistCount: (itemId: string, target: number) => void
  /** Permanently hide the checklist card (progress keeps tracking). */
  dismissChecklist: () => void
  /** Fold the card into its corner pill. No-op when dismissed or already minimized. */
  minimizeChecklist: () => void
  /** Expand the card from its corner pill. No-op unless minimized. */
  reopenChecklist: () => void
  /** Record that a panel's one-time first-touch tour offer was shown. */
  markTipSeen: (kind: string) => void
  /**
   * A first-time core behavior happened. Idempotent per behavior; fires
   * `user_activated` once when the threshold is first reached.
   */
  recordActivationBehavior: (behavior: ActivationBehavior) => void
  /** Wipe (tests). */
  reset: () => void
}

function stamp(): string {
  return new Date().toISOString()
}

function msBetween(fromIso: string | null, toIso: string): number {
  return fromIso ? Date.parse(toIso) - Date.parse(fromIso) : 0
}

/** How the user came through the door; a funnel dimension, never a stage. */
function entryPath(s: OnboardingState): UserActivatedPayload['entry_path'] {
  if (isMigrated(s)) return 'migrated'
  if (!s.startedAt) return 'never_started'
  if (!s.completedAt) return 'abandoned'
  return s.skipped ? 'skipped' : 'completed'
}

/**
 * A record the legacy migration wrote, not a user who skipped. Newer runs
 * stamp `origin`; older ones are recognised by their shape: skipped with no
 * answers and startedAt equal to completedAt, which the app never writes
 * (a real skip lands after the interview has been on screen).
 */
function isMigrated(s: OnboardingState): boolean {
  if (s.origin === 'migration') return true
  return (
    s.skipped &&
    s.startedAt != null &&
    s.startedAt === s.completedAt &&
    Object.keys(s.answers).length === 0
  )
}

function checklistTotal(id: string): number {
  return activeOnboardingConfig().checklists[id]?.items.length ?? 0
}

/**
 * Stamp an item done. Emits `checklist_item_completed` on the transition and
 * `checklist_completed` once, when the last item lands. Shared by the manual
 * tick and the signal-driven count so both paths report identically.
 */
function completeItem(cur: OnboardingState, itemId: string, at: string): OnboardingState {
  const checklist = cur.checklist
  if (!checklist || checklist.done.includes(itemId)) return cur
  const done = [...checklist.done, itemId]
  const total = checklistTotal(checklist.id)
  trackEvent(EVENTS.CHECKLIST_ITEM_COMPLETED, {
    checklist_id: checklist.id,
    item_id: itemId,
    done_count: done.length,
    total,
    persona: cur.persona,
  })
  if (total > 0 && done.length === total) {
    trackEvent(EVENTS.CHECKLIST_COMPLETED, {
      checklist_id: checklist.id,
      persona: cur.persona,
      duration_ms: msBetween(cur.completedAt, at),
    })
    setMilestone('checklist_completed_at')
  }
  return {
    ...cur,
    checklist: { ...checklist, done, doneAt: { ...checklist.doneAt, [itemId]: at } },
  }
}

export const useOnboardingStore = create<OnboardingStoreState>((set, get) => {
  const commit = (next: OnboardingState) => {
    persist(next)
    set(next)
  }
  return {
    // Anonymous until the sync hook sets the scope: nothing to read yet.
    ...EMPTY_ONBOARDING,
    hydrated: false,

    setScope: (scopeId) => {
      const next = scopeId && scopeId !== 'anonymous' ? scopeId : null
      if (next === scope) return
      scope = next
      // A user not yet hydrated this page lifetime waits for their server copy.
      set({ ...readPersisted(), hydrated: next != null && hydratedScopes.has(next) })
    },

    hydrate: (state) => {
      const next = validateOnboardingState(state) ?? EMPTY_ONBOARDING
      persist(next)
      if (scope) hydratedScopes.add(scope)
      set({ ...next, hydrated: true })
    },

    markHydrated: (opts) => {
      if (scope && !opts?.provisional) hydratedScopes.add(scope)
      set({ hydrated: true })
    },

    begin: (configVersion) => {
      const cur = onboardingSnapshot(get())
      if (cur.startedAt) return
      commit({ ...cur, configVersion, startedAt: stamp() })
      trackEvent(EVENTS.ONBOARDING_STARTED, { config_version: configVersion })
    },

    recordAnswer: (questionId, optionId) => {
      const cur = onboardingSnapshot(get())
      // Position in the interview, so drop-off reads by question order even
      // when a `skipIf` rule elides one.
      const index =
        questionId in cur.answers
          ? Object.keys(cur.answers).indexOf(questionId)
          : Object.keys(cur.answers).length
      commit({ ...cur, answers: { ...cur.answers, [questionId]: { optionId, at: stamp() } } })
      trackEvent(EVENTS.ONBOARDING_QUESTION_ANSWERED, {
        question_id: questionId,
        option_id: optionId,
        index,
        config_version: cur.configVersion,
      })
    },

    complete: (persona, checklistId) => {
      const cur = onboardingSnapshot(get())
      const at = stamp()
      commit({
        ...cur,
        persona,
        completedAt: at,
        skipped: false,
        checklist: checklistId
          ? {
              id: checklistId,
              done: [],
              doneAt: {},
              counts: {},
              dismissedAt: null,
              minimized: false,
            }
          : cur.checklist,
      })
      trackEvent(EVENTS.ONBOARDING_COMPLETED, {
        persona,
        config_version: cur.configVersion,
        duration_ms: msBetween(cur.startedAt, at),
      })
      setMilestone('onboarding_completed_at')
    },

    skipAll: () => {
      const cur = onboardingSnapshot(get())
      commit({ ...cur, skipped: true, completedAt: stamp() })
      trackEvent(EVENTS.ONBOARDING_SKIPPED, {
        config_version: cur.configVersion,
        questions_answered: Object.keys(cur.answers).length,
      })
    },

    markChecklistDone: (itemId) => {
      const cur = onboardingSnapshot(get())
      const next = completeItem(cur, itemId, stamp())
      if (next !== cur) commit(next)
    },

    unmarkChecklistDone: (itemId) => {
      const cur = onboardingSnapshot(get())
      if (!cur.checklist || !cur.checklist.done.includes(itemId)) return
      commit({
        ...cur,
        checklist: {
          ...cur.checklist,
          done: cur.checklist.done.filter((d) => d !== itemId),
        },
      })
    },

    incrementChecklistCount: (itemId, target) => {
      const cur = onboardingSnapshot(get())
      if (!cur.checklist || cur.checklist.done.includes(itemId)) return
      const count = (cur.checklist.counts[itemId] ?? 0) + 1
      const counted: OnboardingState = {
        ...cur,
        checklist: { ...cur.checklist, counts: { ...cur.checklist.counts, [itemId]: count } },
      }
      // Only the transition to done is an event; the counts before it are
      // not, or "add 3 tickers" would emit once per ticker.
      commit(count >= target ? completeItem(counted, itemId, stamp()) : counted)
    },

    dismissChecklist: () => {
      const cur = onboardingSnapshot(get())
      if (!cur.checklist) return
      commit({ ...cur, checklist: { ...cur.checklist, dismissedAt: stamp() } })
      const total = checklistTotal(cur.checklist.id)
      const doneCount = cur.checklist.done.length
      // A finish and a give-up both arrive here; the counts tell them apart.
      trackEvent(EVENTS.CHECKLIST_DISMISSED, {
        checklist_id: cur.checklist.id,
        done_count: doneCount,
        total,
        complete: total > 0 && doneCount >= total,
      })
    },

    minimizeChecklist: () => {
      const cur = onboardingSnapshot(get())
      const checklist = cur.checklist
      if (!checklist || checklist.dismissedAt || checklist.minimized) return
      commit({ ...cur, checklist: { ...checklist, minimized: true } })
      trackEvent(EVENTS.CHECKLIST_MINIMIZED, {
        checklist_id: checklist.id,
        done_count: checklist.done.length,
        total: checklistTotal(checklist.id),
      })
    },

    reopenChecklist: () => {
      const cur = onboardingSnapshot(get())
      const checklist = cur.checklist
      if (!checklist || checklist.dismissedAt || !checklist.minimized) return
      commit({ ...cur, checklist: { ...checklist, minimized: false } })
      trackEvent(EVENTS.CHECKLIST_REOPENED, {
        checklist_id: checklist.id,
        done_count: checklist.done.length,
        total: checklistTotal(checklist.id),
      })
    },

    markTipSeen: (kind) => {
      const cur = onboardingSnapshot(get())
      if (cur.tipsSeen.includes(kind)) return
      commit({ ...cur, tipsSeen: [...cur.tipsSeen, kind] })
    },

    recordActivationBehavior: (behavior) => {
      const cur = onboardingSnapshot(get())
      if (cur.activation.behaviors[behavior]) return
      const at = stamp()
      const behaviors = { ...cur.activation.behaviors, [behavior]: at }
      const reached = Object.keys(behaviors).length >= ACTIVATION_THRESHOLD
      const activatedAt = cur.activation.activatedAt ?? (reached ? at : null)
      commit({ ...cur, activation: { behaviors, activatedAt } })
      // Once, on the transition. An already-activated profile hydrated from
      // another device carries activatedAt and never re-fires.
      if (reached && !cur.activation.activatedAt) {
        const first = cur.startedAt ?? Object.values(behaviors).sort()[0]
        trackEvent(EVENTS.USER_ACTIVATED, {
          entry_path: entryPath(cur),
          persona: cur.persona,
          behaviors: Object.keys(behaviors).sort(),
          days_since_first_visit: Math.floor(msBetween(first, at) / 86_400_000),
        })
        setMilestone('user_activated_at')
      }
    },

    reset: () => {
      hydratedScopes.clear()
      persist(EMPTY_ONBOARDING)
      set({ ...EMPTY_ONBOARDING, hydrated: false })
    },
  }
})
